import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  CcdlModalService,
  ModalOptions,
  ModalRef,
} from '@cpm-pr1618001/ng-design-lib';
import {
  Countries,
  DashboardLabelsFacade,
} from '@cpm-pr1618003/ng-initializer-lib';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { MobileMenuComponent } from '../header/mobile-menu/mobile-menu.component';
import { UserDialogView } from '../landing-page.modal';
import { LandingPageFacade } from '../store/facade/landing-page.facade';
import {
  FPDetails,
  SignInDetails,
  SignInResponse,
  SignUpDetails,
  SignUpResponse,
} from '../store/state/landing-page.state';
import { ForgotPasswordComponent } from './forgot-password/forgot-password.component';
import { PrivacyPolicyComponent } from './privacy-policy/privacy-policy.component';
import { ResetUpdateComponent } from './reset-update/reset-update.component';
import { SignInComponent } from './sign-in/sign-in.component';
import { SignUpComponent } from './sign-up/sign-up.component';
import { TermsComponent } from './terms/terms.component';
import { UserPresenter } from './user.presenter';

@Component({
  selector: 'app-user',
  templateUrl: './user.container.component.html',
  styleUrls: ['./user.container.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SignInComponent,
    SignUpComponent,
    ResetUpdateComponent,
    ForgotPasswordComponent,
    MobileMenuComponent,
  ],
  standalone: true,
})
export class UserContainerComponent implements OnInit, OnDestroy {
  activeSignButton$ = this.landingPageFacade.activeSignButton$;
  signInDetails$ = this.landingPageFacade.signInDetails$;
  signUpDetails$ = this.landingPageFacade.signUpDetails$;
  isOTPRequested$ = this.landingPageFacade.isOTPRequested$;
  isPasswordUpdated$ = this.landingPageFacade.isPasswordUpdated$;
  countries$ = this.dashboardLabelsFacade.countries$;
  states$ = this.dashboardLabelsFacade.statesOfSelectedCountry$;
  chosenCountry$ = this.dashboardLabelsFacade.chosenCountry$;

  userForm!: FormGroup;

  private subscription: Subscription = new Subscription();

  chosenCountry: Countries;
  constructor(
    private landingPageFacade: LandingPageFacade,
    private dashboardLabelsFacade: DashboardLabelsFacade,
    private userPresenter: UserPresenter,
    private ccdlModalService: CcdlModalService,
  ) {}

  ngOnInit(): void {
    this.userForm = this.userPresenter.getForm();
    this.subscription.add(
      this.signInDetails$.subscribe((details: SignInResponse) => {
        if (details && details.userId) {
          this.onSignInSuccess(details);
        }
      }),
    );

    this.subscription.add(
      this.signUpDetails$.subscribe((details: SignUpResponse) => {
        if (details) {
          this.userForm.reset();
        }
      }),
    );

    this.subscription.add(
      this.isPasswordUpdated$.subscribe((details: boolean) => {
        if (details) {
          this.userForm.reset();
        }
      }),
    );

    this.subscription.add(
      this.chosenCountry$.subscribe((selectedCountry: Countries) => {
        this.chosenCountry = selectedCountry;
      }),
    );
  }

  onSignInSuccess(details: SignInResponse): void {
    window.location.href = environment.captrixonURL;
  }

  getFormGroup(formGroupName: string): FormGroup {
    return this.userForm.get(formGroupName) as FormGroup;
  }

  forgotPassword() {
    this.landingPageFacade.setActiveSignButton(UserDialogView.FORGOT_PASSWORD);
  }

  signUpView() {
    this.landingPageFacade.setActiveSignButton(UserDialogView.SIGN_UP);
  }

  signInView(): void {
    this.landingPageFacade.setActiveSignButton(UserDialogView.SIGN_IN);
  }

  closeDialogMenu(): void {
    this.landingPageFacade.setActiveSignButton(UserDialogView.CLOSE);
  }

  signInSubmit(): void {
    const signInForm: AbstractControl = this.userForm.get('signInForm');
    if (signInForm.valid) {
      const signInDetails: SignInDetails =
        this.userPresenter.getSignInPayload();
      this.landingPageFacade.signIn(signInDetails);
    }
  }

  signUpSubmit(): void {
    const signUpForm: AbstractControl = this.userForm.get('signUpForm');
    const password: string = signUpForm.get('password').value;
    const confirmPassword: string = signUpForm.get('confirmPassword').value;
    const isPasswordMatched = this.userPresenter.isPasswordMatched(
      password,
      confirmPassword,
    );
    const isRememberMeChecked: boolean = signUpForm.get('rememberMe').value;
    if (signUpForm.valid && isPasswordMatched && isRememberMeChecked) {
      const signUpDetails: SignUpDetails = this.userPresenter.getSignUpPayload(
        this.chosenCountry,
      );
      this.landingPageFacade.signUp(signUpDetails);
    }
  }

  requestOTP(emailId: string): void {
    this.landingPageFacade.requestOTP(emailId);
  }

  resetPasswordSubmit(): void {
    const fpForm: AbstractControl = this.userForm.get('fpForm');
    const password: string = fpForm.get('newPassword').value;
    const confirmPassword: string = fpForm.get('confirmPassword').value;
    const isPasswordMatched = this.userPresenter.isPasswordMatched(
      password,
      confirmPassword,
    );

    if (fpForm.valid && isPasswordMatched) {
      const fpDetails: FPDetails = this.userPresenter.getResetPasswordPayload();
      this.landingPageFacade.resetPassword(fpDetails);
    }
  }

  onTermsEmitter(): void {
    const options: ModalOptions = {
      component: TermsComponent,
      modalButtons: [
        {
          btnLabel: 'Close',
          size: 'md',
          btnType: 'primary',
          actionType: 'CLOSE',
        },
      ],
      freezeIfDailogExists: true,
    };
    const modalRef: ModalRef = this.ccdlModalService.open(options);
    modalRef.result.finally(() => {});
  }

  onPrivacyEmitter(): void {
    const options: ModalOptions = {
      component: PrivacyPolicyComponent,
      modalButtons: [
        {
          btnLabel: 'Close',
          size: 'md',
          btnType: 'primary',
          actionType: 'CLOSE',
        },
      ],
      freezeIfDailogExists: true,
    };
    const modalRef: ModalRef = this.ccdlModalService.open(options);
    modalRef.result.finally(() => {});
  }

  selectedCountry(countryCode: string): void {
    this.dashboardLabelsFacade.chosenCountry(countryCode);
  }

  selectedState(stateCode: string): void {
    this.dashboardLabelsFacade.chosenState(stateCode);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
